import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Search, Hash, Sparkles, Activity, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  CHAT:      { color: '#dcc878', label: 'CHAMBER',     glow: 'rgba(220,200,120,0.4)' },
  WATCH:     { color: '#7dd0ff', label: 'VIGIL',       glow: 'rgba(125,208,255,0.4)' },
  COORD:     { color: '#ff7eb6', label: 'STRINGS',     glow: 'rgba(255,126,182,0.45)' },
  DECREE:    { color: '#bfa15a', label: 'DECREE',      glow: 'rgba(191,161,90,0.5)' },
  SOUL_CARD: { color: '#a08850', label: 'SOUL CARD',   glow: 'rgba(160,136,80,0.4)' },
  TOKEN_CALL:{ color: '#5be584', label: 'TOKEN CALL',  glow: 'rgba(91,229,132,0.4)' },
  MISC:      { color: '#9a8ba6', label: 'OTHER',       glow: 'rgba(154,139,166,0.4)' },
};

function shortAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff/60)}m`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
  } catch { return ''; }
}

const FILTERS = [
  { key: 'ALL',     label: 'All' },
  { key: 'CHAT',    label: 'Chamber' },
  { key: 'WATCH',   label: 'Vigil' },
  { key: 'COORD',   label: 'Strings' },
  { key: 'DECREE',  label: 'Decree' },
];

function StatTile({ value, label, accent = '#bfa15a', testid, delta = 0 }) {
  const [pulseKey, setPulseKey] = useState(0);
  const [floatDelta, setFloatDelta] = useState(null);
  useEffect(() => {
    if (delta && delta > 0) {
      setPulseKey((k) => k + 1);
      setFloatDelta({ key: Date.now(), n: delta });
      const t = setTimeout(() => setFloatDelta(null), 1700);
      return () => clearTimeout(t);
    }
  }, [delta, value]);

  return (
    <div className="flex flex-col items-start relative" data-testid={testid}>
      <span
        key={pulseKey}
        className="font-mono"
        style={{
          fontSize: 'clamp(2rem, 4.5vw, 3.4rem)',
          lineHeight: 1,
          color: accent,
          textShadow: `0 0 28px ${accent}55`,
          fontWeight: 800,
          animation: pulseKey ? 'codex-stat-pulse 1.1s ease-out' : 'none',
        }}
      >
        {(Number(value) || 0).toLocaleString()}
      </span>
      {floatDelta ? (
        <span
          key={floatDelta.key}
          className="font-mono absolute"
          style={{
            top: -8,
            left: 0,
            fontSize: '0.78rem',
            color: '#5be584',
            textShadow: '0 0 10px rgba(91,229,132,0.7)',
            fontWeight: 800,
            animation: 'codex-delta-rise 1.6s ease-out forwards',
            pointerEvents: 'none',
          }}
        >
          +{floatDelta.n}
        </span>
      ) : null}
      <span
        className="uppercase font-mono mt-2"
        style={{
          fontSize: '0.6rem',
          letterSpacing: '0.32em',
          color: 'rgba(230,220,200,0.5)',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/* ─── Live toast — appears when corpus grows ───────────────── */
function LiveToast({ id, total, vocab, source }) {
  const meta = SOURCE_META[source] || SOURCE_META.MISC;
  return (
    <div
      data-testid={`codex-live-toast-${id}`}
      className="relative px-4 py-3 rounded-sm overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(15,8,20,0.95) 0%, rgba(8,4,14,0.95) 100%)',
        border: `1px solid ${meta.color}66`,
        boxShadow: `0 0 22px ${meta.glow}, inset 0 0 22px rgba(0,0,0,0.6)`,
        minWidth: 260,
        animation: 'codex-toast-in 0.5s cubic-bezier(.2,.8,.2,1)',
      }}
    >
      <div
        className="pointer-events-none absolute inset-y-0 left-0"
        style={{ width: 3, background: meta.color, boxShadow: `0 0 8px ${meta.color}` }}
      />
      <div className="flex items-center gap-2 mb-1">
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: '#5be584', boxShadow: '0 0 8px #5be584' }}
        />
        <span
          className="font-mono uppercase"
          style={{ fontSize: '0.58rem', letterSpacing: '0.28em', color: meta.color }}
        >
          {meta.label} · The Chancellor speaks
        </span>
      </div>
      <div
        className="font-mono"
        style={{ fontSize: '0.82rem', color: '#e6dcc8', letterSpacing: '0.04em' }}
      >
        +{total} {total === 1 ? 'utterance' : 'utterances'}
        {vocab > 0 ? (
          <span style={{ color: 'rgba(230,220,200,0.55)' }}>
            {' '}· vocab grew by {vocab}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function UtteranceRow({ u, score }) {
  const meta = SOURCE_META[u.source] || SOURCE_META.MISC;
  const ago = shortAgo(u.created_at);
  const md = u.metadata || {};
  const tag = [md.symbol && `$${md.symbol}`, md.pattern, md.wallet_short]
    .filter(Boolean).slice(0, 3);
  return (
    <div
      className="grid items-start gap-3 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 transition-colors hover:bg-white/[0.025] grid-cols-[80px_1fr] sm:grid-cols-[110px_1fr_80px]"
      style={{
        borderBottom: '1px solid rgba(220,200,120,0.06)',
      }}
      data-testid={`codex-row-${u.utterance_id}`}
    >
      <div className="flex flex-col gap-1.5">
        <span
          className="font-mono uppercase px-2 py-0.5 rounded-sm text-center"
          style={{
            fontSize: '0.6rem',
            letterSpacing: '0.18em',
            color: meta.color,
            background: 'rgba(0,0,0,0.4)',
            border: `1px solid ${meta.color}55`,
            boxShadow: `0 0 10px ${meta.glow}`,
          }}
        >
          {meta.label}
        </span>
        <span
          className="font-mono"
          style={{ fontSize: '0.62rem', color: 'rgba(230,220,200,0.42)', textAlign: 'center' }}
        >
          T-{ago}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <p
          className="italic"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: '1rem',
            lineHeight: 1.5,
            color: 'rgba(230,220,200,0.92)',
          }}
        >
          {u.text}
        </p>
        {tag.length ? (
          <div className="flex items-center gap-2 flex-wrap">
            {tag.map((t, i) => (
              <span
                key={`${i}-${t}`}
                className="font-mono uppercase"
                style={{
                  fontSize: '0.55rem',
                  letterSpacing: '0.14em',
                  color: 'rgba(220,200,120,0.55)',
                  padding: '1px 6px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 2,
                }}
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex justify-end items-start pt-1 hidden sm:flex">
        {typeof score === 'number' ? (
          <span
            className="font-mono"
            style={{
              fontSize: '0.65rem',
              color: 'rgba(220,200,120,0.55)',
              letterSpacing: '0.12em',
            }}
            title="cosine similarity"
          >
            {score.toFixed(3)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function CodexPage() {
  const [stats, setStats] = useState({ total: 0, indexed: 0, vocab_size: 0, by_source: [] });
  const [recent, setRecent] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [delta, setDelta] = useState({ total: 0, vocab: 0 });
  const [toasts, setToasts] = useState([]);
  const prevStatsRef = useRef(null);
  const debounceRef = useRef(null);

  const dominantNewSource = (prev, next) => {
    if (!prev || !next) return 'WATCH';
    const prevMap = Object.fromEntries((prev.by_source || []).map((s) => [s.source, s.count]));
    const nextMap = Object.fromEntries((next.by_source || []).map((s) => [s.source, s.count]));
    let best = { source: 'WATCH', delta: 0 };
    for (const k of Object.keys(nextMap)) {
      const d = (nextMap[k] || 0) - (prevMap[k] || 0);
      if (d > best.delta) best = { source: k, delta: d };
    }
    return best.source;
  };

  const loadStats = async () => {
    try {
      const r = await fetch(`${API_URL}/api/chancellor/codex/stats`);
      const j = await r.json();
      const next = j || {};
      const prev = prevStatsRef.current;
      if (prev) {
        const dt = (next.total || 0) - (prev.total || 0);
        const dv = (next.vocab_size || 0) - (prev.vocab_size || 0);
        if (dt > 0) {
          setDelta({ total: dt, vocab: Math.max(0, dv) });
          const src = dominantNewSource(prev, next);
          const id = Date.now();
          setToasts((cur) => [...cur, { id, total: dt, vocab: Math.max(0, dv), source: src }]);
          setTimeout(() => {
            setToasts((cur) => cur.filter((t) => t.id !== id));
          }, 4400);
          // Pull fresh recent list so the new utterance is visible at top
          loadRecent(filter);
        }
      }
      prevStatsRef.current = next;
      setStats(next);
    } catch {/* silent */}
  };

  const loadRecent = async (src) => {
    try {
      const u = new URL(`${API_URL}/api/chancellor/codex/recent`);
      u.searchParams.set('limit', '40');
      if (src && src !== 'ALL') u.searchParams.set('source', src);
      const r = await fetch(u.toString());
      const j = await r.json();
      setRecent(j?.utterances || []);
    } catch {/* silent */}
  };

  const statsTickRef = useRef(loadStats);
  statsTickRef.current = loadStats;

  useEffect(() => {
    loadStats();
    // Poll every 15s — backend's 90s reindex TTL means vocab updates lag,
    // but `total` reflects the live count instantly on insert.
    const t = setInterval(() => statsTickRef.current(), 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { loadRecent(filter); }, [filter]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const u = new URL(`${API_URL}/api/chancellor/codex/search`);
        u.searchParams.set('q', query.trim());
        u.searchParams.set('limit', '20');
        if (filter !== 'ALL') u.searchParams.set('source', filter);
        const r = await fetch(u.toString());
        const j = await r.json();
        setResults(j);
      } catch {
        setResults({ matches: [], count: 0 });
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [query, filter]);

  const showing = useMemo(() => {
    if (results) return results.matches.map((m) => ({ u: m.doc, score: m.score }));
    return recent.map((u) => ({ u, score: null }));
  }, [results, recent]);

  return (
    <div
      className="relative min-h-screen w-full overflow-x-clip"
      data-testid="codex-page"
      style={{ background: '#050208', color: '#e6dcc8' }}
    >
      <style>{`
        @keyframes codex-cursor-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes codex-grain {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-1%, 1%); }
          50% { transform: translate(1%, -1%); }
          75% { transform: translate(-1%, -1%); }
        }
        @keyframes codex-stat-pulse {
          0%   { transform: scale(1);   filter: brightness(1); }
          25%  { transform: scale(1.06); filter: brightness(1.5); }
          100% { transform: scale(1);   filter: brightness(1); }
        }
        @keyframes codex-delta-rise {
          0%   { transform: translate(0, 8px);  opacity: 0; }
          15%  { transform: translate(0, 0);    opacity: 1; }
          70%  { transform: translate(0, -28px); opacity: 1; }
          100% { transform: translate(0, -42px); opacity: 0; }
        }
        @keyframes codex-toast-in {
          0%   { transform: translateX(80px) scale(0.94); opacity: 0; }
          100% { transform: translateX(0) scale(1);       opacity: 1; }
        }
        .codex-scroll::-webkit-scrollbar { width: 6px; }
        .codex-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
        .codex-scroll::-webkit-scrollbar-thumb { background: rgba(220,200,120,0.18); border-radius: 3px; }
      `}</style>

      {/* Atmospheric layers */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background:
            'radial-gradient(circle at 30% 0%, rgba(120,30,30,0.16) 0%, transparent 50%), ' +
            'radial-gradient(circle at 70% 100%, rgba(40,10,30,0.20) 0%, transparent 50%), ' +
            'linear-gradient(180deg, #050208 0%, #0a0410 60%, #050208 100%)',
        }}
      />
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"180\\" height=\\"180\\"><filter id=\\"n\\"><feTurbulence baseFrequency=\\"0.85\\"/></filter><rect width=\\"100%\\" height=\\"100%\\" filter=\\"url(%23n)\\" opacity=\\"0.6\\"/></svg>")',
          animation: 'codex-grain 8s steps(4) infinite',
        }}
      />

      {/* Top bar */}
      <div
        className="sticky top-0 z-20 flex items-center justify-between px-3 sm:px-6 lg:px-10 pr-16 md:pr-6 lg:pr-10 py-3 sm:py-4 border-b gap-2"
        style={{ borderColor: 'rgba(220,200,120,0.18)', background: 'rgba(5,2,8,0.85)', backdropFilter: 'blur(6px)' }}
      >
        <Link
          to="/halls"
          className="inline-flex items-center gap-1.5 sm:gap-2 font-mono uppercase tracking-[0.22em] sm:tracking-[0.28em] transition-all hover:scale-[1.04] shrink-0"
          style={{
            padding: '7px 11px',
            fontSize: '0.62rem',
            color: '#ffd680',
            background: 'linear-gradient(180deg, rgba(40,12,8,0.6) 0%, rgba(20,6,4,0.85) 100%)',
            border: '1px solid rgba(200,144,72,0.55)',
            borderRadius: 2,
            boxShadow: '0 0 14px rgba(200,144,72,0.28), inset 0 0 14px rgba(0,0,0,0.5)',
            textDecoration: 'none',
            textShadow: '0 0 8px rgba(200,144,72,0.5)',
          }}
          data-testid="codex-back-court"
        >
          <ArrowLeft size={13} />
          <span className="hidden sm:inline">Halls of the Chancellor</span>
          <span className="sm:hidden">Halls</span>
        </Link>
        <div className="hidden md:flex items-center gap-2">
          <Sparkles size={11} style={{ color: '#bfa15a' }} />
          <span
            className="font-mono uppercase"
            style={{ fontSize: '0.62rem', letterSpacing: '0.32em', color: 'rgba(230,220,200,0.65)' }}
          >
            TF-IDF Index · No external embeddings
          </span>
        </div>
        <Link
          to="/feed"
          className="inline-flex items-center gap-1 sm:gap-2 font-mono uppercase tracking-[0.22em] sm:tracking-[0.28em] hover:opacity-80 shrink-0"
          style={{ color: '#bfa15a', fontSize: '0.66rem' }}
          data-testid="codex-feed-link"
        >
          <span className="hidden sm:inline">The Vigil</span>
          <span className="sm:hidden">Vigil</span>
          <Activity size={12} />
        </Link>
      </div>

      {/* Hero */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-10 pt-8 sm:pt-10 pb-6">
        <div className="flex items-center gap-3 mb-3">
          <BookOpen size={14} style={{ color: '#bfa15a' }} />
          <span
            className="font-mono uppercase"
            style={{ fontSize: '0.66rem', letterSpacing: '0.36em', color: '#bfa15a' }}
          >
            The Chancellor's Codex
          </span>
        </div>
        <h1
          className="mb-3"
          style={{
            fontFamily: '"Cinzel", Georgia, serif',
            fontSize: 'clamp(1.7rem, 5.5vw, 4rem)',
            lineHeight: 1.05,
            color: '#e6dcc8',
            letterSpacing: '0.02em',
          }}
        >
          Every word he has spoken
          <span
            className="inline-block ml-2"
            style={{ animation: 'codex-cursor-blink 1.1s steps(1) infinite', color: '#bfa15a' }}
          >
            ▌
          </span>
        </h1>
        <p
          className="italic max-w-3xl"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)',
            lineHeight: 1.55,
            color: 'rgba(230,220,200,0.72)',
          }}
        >
          A pure-numpy TF-IDF index over every utterance the Chancellor has produced. No external embedding API.
          Each new narration extends the corpus, and every Claude call is anchored to past utterances retrieved
          from this index. The voice does not drift. The Chancellor remembers itself.
        </p>
      </div>

      {/* Stats strip */}
      <div
        className="relative z-10 mx-3 sm:mx-6 lg:mx-10 mb-6 grid gap-4"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          padding: '20px 22px',
          background: 'linear-gradient(180deg, rgba(220,200,120,0.06) 0%, rgba(0,0,0,0) 100%)',
          border: '1px solid rgba(220,200,120,0.18)',
          borderRadius: 2,
        }}
        data-testid="codex-stats"
      >
        <StatTile value={stats.total}      label="Utterances"  accent="#dcc878" testid="codex-stat-total"   delta={delta.total} />
        <StatTile value={stats.indexed}    label="Indexed"     accent="#bfa15a" testid="codex-stat-indexed" delta={delta.total} />
        <StatTile value={stats.vocab_size} label="Vocabulary"  accent="#a08850" testid="codex-stat-vocab"   delta={delta.vocab} />
        <div className="flex flex-col gap-1.5">
          <span
            className="font-mono uppercase"
            style={{ fontSize: '0.6rem', letterSpacing: '0.32em', color: 'rgba(230,220,200,0.5)' }}
          >
            By Source
          </span>
          <div className="flex flex-col gap-1">
            {(stats.by_source || []).slice(0, 5).map((s) => {
              const m = SOURCE_META[s.source] || SOURCE_META.MISC;
              return (
                <div key={s.source} className="flex items-center gap-2">
                  <span
                    className="font-mono uppercase"
                    style={{ fontSize: '0.6rem', letterSpacing: '0.16em', color: m.color, width: 70 }}
                  >
                    {m.label}
                  </span>
                  <span
                    className="font-mono"
                    style={{ fontSize: '0.7rem', color: '#e6dcc8' }}
                  >
                    {s.count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative z-10 mx-3 sm:mx-6 lg:mx-10 mb-4">
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-sm"
          style={{
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(220,200,120,0.32)',
            boxShadow: '0 0 22px rgba(220,200,120,0.08)',
          }}
        >
          <Search size={14} style={{ color: '#bfa15a' }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask the Codex (e.g., 'panic sell coward', 'diamond hands', 'rug warning')…"
            className="flex-1 bg-transparent outline-none font-mono"
            style={{ color: '#e6dcc8', fontSize: '0.9rem', letterSpacing: '0.02em' }}
            data-testid="codex-search-input"
          />
          {query ? (
            <button
              onClick={() => setQuery('')}
              className="p-1 rounded-sm hover:bg-white/5"
              data-testid="codex-search-clear"
            >
              <X size={12} style={{ color: '#bfa15a' }} />
            </button>
          ) : null}
          {searching ? (
            <span
              className="font-mono uppercase"
              style={{ fontSize: '0.55rem', letterSpacing: '0.22em', color: 'rgba(220,200,120,0.5)' }}
            >
              searching…
            </span>
          ) : null}
        </div>
      </div>

      {/* Filter chips */}
      <div className="relative z-10 mx-3 sm:mx-6 lg:mx-10 mb-4 flex flex-wrap gap-2" data-testid="codex-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className="font-mono uppercase px-3 py-1.5 rounded-sm transition-all"
            style={{
              fontSize: '0.62rem',
              letterSpacing: '0.18em',
              background: filter === f.key ? 'rgba(220,200,120,0.14)' : 'transparent',
              border: filter === f.key ? '1px solid rgba(220,200,120,0.4)' : '1px solid rgba(220,200,120,0.12)',
              color: filter === f.key ? '#dcc878' : 'rgba(230,220,200,0.55)',
            }}
            data-testid={`codex-filter-${f.key.toLowerCase()}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Result list */}
      <div className="relative z-10 mx-3 sm:mx-6 lg:mx-10 pb-20">
        <div
          className="rounded-sm"
          style={{
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(220,200,120,0.18)',
            boxShadow: 'inset 0 0 80px rgba(0,0,0,0.5)',
          }}
        >
          <div
            className="flex items-center justify-between px-5 py-3 border-b"
            style={{ borderColor: 'rgba(220,200,120,0.12)' }}
          >
            <span
              className="font-mono uppercase"
              style={{ fontSize: '0.62rem', letterSpacing: '0.22em', color: '#bfa15a' }}
            >
              <Hash size={11} className="inline -mt-0.5 mr-1" />
              {results ? 'Top Matches' : 'Recent Utterances'}
            </span>
            <span
              className="font-mono uppercase"
              style={{ fontSize: '0.6rem', letterSpacing: '0.22em', color: 'rgba(230,220,200,0.4)' }}
            >
              {showing.length} {showing.length === 1 ? 'result' : 'results'}
            </span>
          </div>

          <div className="codex-scroll" data-testid="codex-results">
            {showing.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <BookOpen size={32} style={{ color: 'rgba(220,200,120,0.4)', marginBottom: 14 }} />
                <p
                  className="italic"
                  style={{
                    fontFamily: '"EB Garamond", serif',
                    fontSize: '1rem',
                    color: 'rgba(230,220,200,0.5)',
                  }}
                >
                  {query ? 'The Chancellor has not yet spoken on this matter.' : 'The Codex is empty.'}
                </p>
              </div>
            ) : (
              showing.map((row) => (
                <UtteranceRow key={row.u.utterance_id} u={row.u} score={row.score} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Live toast stack — appears bottom-right when codex grows */}
      <div
        className="fixed z-50 flex flex-col gap-2"
        style={{ right: 24, bottom: 24, pointerEvents: 'none' }}
        data-testid="codex-toast-stack"
      >
        {toasts.map((t) => (
          <LiveToast key={t.id} id={t.id} total={t.total} vocab={t.vocab} source={t.source} />
        ))}
      </div>
    </div>
  );
}
